import log from 'loglevel'
import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import request from 'apis/request'
import { proxyLogin, singinForAgent } from 'commons/firebase-auth'
import Page from 'commons/page'
import Block from 'components/block'
import ButtonV2 from 'components/button-v2'
import { PageTitle, Text } from 'components/typography'
import { JWT_TOKEN_KEY_NAME, LOGIN_USER_MODE } from 'constants/common'
import { LoginUserModeEnum } from 'constants/loginusermode'
import { ErrorMsg } from 'constants/message'
import ROUTING_PATH from 'constants/route'
import { useAuthProvider } from 'contexts/auth'
import { AuthErrorCodes, getAuth, signOut } from 'firebase/auth'
import { useAppDispatch } from 'hooks'
import { UserRole } from 'schema/user'
import { setErrors } from 'stores/error'

const AgentLogin = () => {
  const { currentUser, claims } = useAuthProvider()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const handleImpersonation = useCallback(async () => {
    const isImpersonation = searchParams.get('impersonation')
    const accessKey = searchParams.get('key')
    if (isImpersonation && accessKey) {
      try {
        const auth = getAuth()
        await signOut(auth)

        const res = await request<{ token?: string; redirect_url?: string }>({
          method: 'post',
          url: `/agents/impersonation-signin`,
          data: { accessKey },
          requireAuth: false,
        })

        if (res.data.token) {
          const proxyToken = await proxyLogin(res.data.token as string)
          const token = await proxyToken.getIdToken()
          localStorage.setItem(JWT_TOKEN_KEY_NAME, token)
          localStorage.setItem(LOGIN_USER_MODE, LoginUserModeEnum.Impersonation)
          navigate(ROUTING_PATH.C0201)
        } else if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url
        } else {
          throw new Error('予期せぬエラー')
        }
      } catch (e: any) {
        log.error(e)
      }
    }
  }, [searchParams, navigate])

  useEffect(() => {
    handleImpersonation()
  }, [handleImpersonation])

  useEffect(() => {
    if (currentUser && claims && claims?.role === UserRole.AGENT) {
      navigate(ROUTING_PATH.C0201)
    }
  }, [claims, currentUser, navigate])

  const handleSubmit = useCallback(async () => {
    try {
      await singinForAgent()
      navigate(ROUTING_PATH.C0201)
    } catch (e: any) {
      log.error(e)
      if (e.code === AuthErrorCodes.POPUP_CLOSED_BY_USER) {
        dispatch(setErrors({ login: { message: ErrorMsg.FORBIDDEN } }))
      }
    }
  }, [dispatch, navigate])

  return (
    <Page title="ログイン" type="guest" width="400px">
      <PageTitle>エージェント向けログインページ</PageTitle>
      <Block>
        <ButtonV2
          variant="secondary"
          size="full"
          onClick={handleSubmit}
          data-testid="social-login"
        >
          <img src="/image/google-icon.svg" alt="Google logo" />
          <span>Google でログイン</span>
        </ButtonV2>
      </Block>
      <Block>
        <Text size="md">
          これは売主様と媒介契約を結んだエージェント向けのツールのログインページです。
        </Text>
        <Text size="md">
          ログインにはTerassのGoogleアカウントを使用してください
        </Text>
      </Block>
    </Page>
  )
}

export default AgentLogin
